import * as React from 'react';
import { Check, ChevronsUpDown, Loader2, Plus } from 'lucide-react';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

// SearchableSelect: combobox com busca server-side (debounce) ou client-side.
// Espelha apps/web/js/components/searchable-select.js. Suporta:
// - allowCreate: callback pra criar inline novo item (nao-existente em onSearch)
// - server: onSearch chamado com debounce 300ms
// - client: lista pre-carregada filtrada localmente

export interface SearchableSelectOption {
  value: string;
  label: string;
  hint?: string;
}

export interface SearchableSelectProps {
  // Lista pre-carregada (modo client). Se omitida, usa onSearch.
  options?: SearchableSelectOption[];
  // Busca server-side: chamada com debounce 300ms.
  onSearch?: (query: string) => Promise<SearchableSelectOption[]>;
  value: string | null;
  onChange: (value: string | null, option: SearchableSelectOption | null) => void;
  placeholder?: string;
  emptyMessage?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  // Habilita criar novo item via callback (recebe o texto digitado).
  allowCreate?: {
    label?: string; // default "Criar X"
    onCreate: (text: string) => Promise<SearchableSelectOption>;
  };
  className?: string;
  // Render custom da opcao selecionada (default: option.label)
  renderSelected?: (option: SearchableSelectOption | null) => React.ReactNode;
  // Renderiza o dropdown inline (sem Portal). Usar dentro de Dialog para
  // evitar conflito de scroll-lock do Radix (veja popover.tsx).
  disablePortal?: boolean;
  // Opcao ja conhecida pelo pai (ex: detail vindo do backend) usada
  // como label inicial no modo server-side, antes do user abrir o
  // popover e disparar onSearch. Evita o trigger exibir placeholder
  // quando ja ha valor pre-existente.
  initialOption?: SearchableSelectOption | null;
  // id do botao trigger, para associar um <Label htmlFor> externo.
  id?: string;
}

const DEBOUNCE_MS = 300;

export function SearchableSelect({
  options: clientOptions,
  onSearch,
  value,
  onChange,
  placeholder = 'Selecionar...',
  emptyMessage = 'Nada encontrado.',
  searchPlaceholder = 'Buscar...',
  disabled,
  allowCreate,
  className,
  renderSelected,
  disablePortal = false,
  initialOption = null,
  id,
}: SearchableSelectProps) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const [serverOptions, setServerOptions] = React.useState<SearchableSelectOption[]>([]);
  const [searching, setSearching] = React.useState(false);
  const [creating, setCreating] = React.useState(false);
  // Cache da ultima opcao selecionada. Necessario no modo server-side
  // porque serverOptions e resetado ao fechar o popover, e sem cache o
  // trigger volta a exibir o placeholder em vez do label selecionado.
  // Inicializado com initialOption se fornecida (cobre caso de detail
  // vindo do backend com vinculos pre-existentes).
  const [lastSelected, setLastSelected] = React.useState<SearchableSelectOption | null>(
    initialOption,
  );
  const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSearchRef = React.useRef<string>('');

  // Sincroniza cache quando initialOption muda (ex: pos-save refetch). Nao
  // sobrescreve o que o user acabou de escolher: o pai pode montar
  // initialOption com o value ATUAL e o label do valor PERSISTIDO, e ai o
  // trigger voltava a mostrar o nome antigo depois de trocar a opcao.
  React.useEffect(() => {
    if (initialOption && initialOption.value === value && lastSelected?.value !== value) {
      setLastSelected(initialOption);
    }
  }, [initialOption, value, lastSelected]);

  const clientFiltered = React.useMemo(() => {
    if (!clientOptions) return [];
    const q = query.trim().toLowerCase();
    if (!q) return clientOptions;
    return clientOptions.filter(
      (o) =>
        o.label.toLowerCase().includes(q) ||
        o.value.toLowerCase().includes(q) ||
        (o.hint?.toLowerCase().includes(q) ?? false),
    );
  }, [clientOptions, query]);

  // Server-side: dispara busca com debounce quando query muda.
  React.useEffect(() => {
    if (!onSearch || !open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = query;
    debounceRef.current = setTimeout(async () => {
      lastSearchRef.current = q;
      setSearching(true);
      try {
        const results = await onSearch(q);
        if (lastSearchRef.current === q) {
          setServerOptions(results);
        }
      } finally {
        if (lastSearchRef.current === q) setSearching(false);
      }
    }, DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, onSearch, open]);

  // Reset state ao fechar.
  React.useEffect(() => {
    if (!open) {
      setQuery('');
      setServerOptions([]);
    }
  }, [open]);

  const visibleOptions = clientOptions ? clientFiltered : serverOptions;
  const selected = clientOptions
    ? (clientOptions.find((o) => o.value === value) ?? null)
    : (serverOptions.find((o) => o.value === value) ??
        (lastSelected?.value === value ? lastSelected : null));

  // Sincroniza cache quando o pai zera value (clear externo).
  React.useEffect(() => {
    if (value === null && lastSelected !== null) {
      setLastSelected(null);
    }
  }, [value, lastSelected]);

  // Mostra item "Criar X" quando query nao bate em nenhum existente.
  const canCreate =
    allowCreate &&
    query.trim().length > 0 &&
    !visibleOptions.some(
      (o) => o.label.toLowerCase() === query.trim().toLowerCase(),
    );

  async function handleCreate() {
    if (!allowCreate) return;
    setCreating(true);
    try {
      const created = await allowCreate.onCreate(query.trim());
      setLastSelected(created);
      onChange(created.value, created);
      setOpen(false);
    } finally {
      setCreating(false);
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          id={id}
          type="button"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            'flex h-10 w-full items-center justify-between rounded-md border border-border bg-surface px-3 py-2 text-sm shadow-soft hover:bg-surface-soft focus-visible:outline-none focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/15 disabled:cursor-not-allowed disabled:opacity-70',
            !selected && 'text-text-subtle',
            className,
          )}
        >
          <span className="truncate text-left">
            {renderSelected ? renderSelected(selected) : (selected?.label ?? placeholder)}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 text-text-subtle" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
        disablePortal={disablePortal}
      >
        {/* shouldFilter=false: o filtro e nosso (clientFiltered por label,
            value e hint, ou o servidor via onSearch). O filtro do cmdk usa so o
            `value` do item (um id/uuid), entao ligado ele escondia as opcoes ao
            buscar pelo nome. */}
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={searchPlaceholder}
            value={query}
            onValueChange={setQuery}
          />
          {/* onWheel: impede que um scroll-lock (react-remove-scroll de um
              Dialog aberto, ou de quem envolver o popover) intercepte a roda
              do mouse no document e deixe a lista rolando so pela barra. */}
          <CommandList onWheel={(e: React.WheelEvent<HTMLDivElement>) => e.stopPropagation()}>
            {searching ? (
              <div className="flex items-center justify-center gap-2 py-6 text-sm text-text-muted">
                <Loader2 className="h-4 w-4 animate-spin" />
                Buscando...
              </div>
            ) : visibleOptions.length === 0 && !canCreate ? (
              <CommandEmpty>{emptyMessage}</CommandEmpty>
            ) : null}

            {visibleOptions.length > 0 ? (
              <CommandGroup>
                {visibleOptions.map((option) => (
                  <CommandItem
                    key={option.value}
                    value={option.value}
                    onSelect={() => {
                      setLastSelected(option);
                      onChange(option.value, option);
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        value === option.value ? 'opacity-100' : 'opacity-0',
                      )}
                    />
                    <div className="flex flex-col">
                      <span>{option.label}</span>
                      {option.hint ? (
                        <span className="text-xs text-text-muted">{option.hint}</span>
                      ) : null}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            ) : null}

            {canCreate ? (
              <CommandGroup>
                <CommandItem
                  value={`__create__${query}`}
                  onSelect={() => {
                    if (!creating) handleCreate();
                  }}
                >
                  {creating ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Plus className="mr-2 h-4 w-4 text-primary" />
                  )}
                  <span className="text-primary font-medium">
                    {allowCreate?.label ?? `Criar "${query}"`}
                  </span>
                </CommandItem>
              </CommandGroup>
            ) : null}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
